// frontend/src/pages/Login.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faArrowRight,
  faSpinner,
  faUser,
  faClock,
  faTimesCircle,
  faEye,
  faEyeSlash,
  faCheckCircle,
  faKey,
} from "@fortawesome/free-solid-svg-icons";
import SEO from "../components/SEO/SEO";
import "../styles/auth.scss";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.canLogin === false) {
        setInfo(
          result.loginMessage ||
            "Your account is pending approval or has been restricted."
        );
        setLoading(false);
        return;
      }

      if (result.isTeamMember && !result.isApproved) {
        setInfo(
          "Your team registration is pending approval. Please wait for an administrator to approve your account."
        );
        setLoading(false);
        return;
      }

      if (
        result.userType === "supplier" &&
        result.supplier &&
        !result.supplier.isApproved
      ) {
        setInfo(
          "Your supplier registration is pending approval. Please wait for an administrator to approve your account."
        );
        setLoading(false);
        return;
      }

      if (!result.isActive) {
        setError("Your account has been deactivated. Please contact support.");
        setLoading(false);
        return;
      }

      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "Invalid email or password";
      if (
        errorMsg.includes("pending approval") ||
        errorMsg.includes("approval")
      ) {
        setInfo(errorMsg);
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Login - Kenali Enterprise"
        description="Login to your Kenali Enterprise account to manage orders, track shipments, and access premium livestock nutrition products."
        keywords="login, Kenali Enterprise, livestock feed, farmer account"
        canonical="https://kenalienterprise.com/login"
      />
      <motion.div
        className="auth-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="auth-container auth-login">
          <div className="auth-header">
            <FontAwesomeIcon icon={faUser} className="auth-icon" />
            <h1>Welcome Back</h1>
            <p>Login to your Kenali Enterprise account</p>
          </div>

          {error && (
            <div className="auth-error">
              <FontAwesomeIcon icon={faTimesCircle} /> {error}
            </div>
          )}

          {info && (
            <div className="auth-info">
              <FontAwesomeIcon icon={faClock} /> {info}
            </div>
          )}

          <form onSubmit={handleLogin} className="auth-form">
            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faEnvelope} /> Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faLock} /> Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              <div className="input-hint">
                <Link to="/forgot-password">
                  <FontAwesomeIcon icon={faKey} /> Forgot password?
                </Link>
              </div>
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Logging in...
                </>
              ) : (
                <>
                  Login <FontAwesomeIcon icon={faArrowRight} />
                </>
              )}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Don't have an account? <Link to="/register">Register here</Link>
            </p>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Login;